import { buttonClasses } from '@mui/material'
import { blue, brightBlue, gray, red, uiColors } from '~/common/styles/colors'
import { cardShadow, depth } from '~/common/styles/depths'
import { typography } from '~/common/styles/typography'
import { ButtonVariant } from '../types'

export const getButtonBasePrimitives = (floating: boolean) => ({
  display: 'flex',
  alignItems: 'center',
  paddingBlock: '0px',
  paddingInline: '12px',
  boxShadow: floating ? depth[2] : 'none',
  border: '2px solid transparent',
  borderRadius: '4px',
  textWrap: 'nowrap',
  fontWeight: 'normal',
  '&:focus-visible': {
    border: `2px solid ${blue[9]}`,
    outline: `1px solid ${uiColors.content.white}`,
    outlineOffset: '-3px',
  },
  [`&.${buttonClasses.sizeLarge}`]: {
    height: '40px',
    ...typography.body3,
    [`& .${buttonClasses.startIcon}`]: {
      '& > *': {
        fontSize: '16px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '8px',
      marginInlineStart: '-4px',
    },
    [`& .${buttonClasses.endIcon}`]: {
      '& > *': {
        fontSize: '16px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '-4px',
      marginInlineStart: '8px',
    },
  },
  [`&.${buttonClasses.sizeMedium}`]: {
    height: '36px',
    ...typography.body2,
    [`& .${buttonClasses.startIcon}`]: {
      '& > *': {
        fontSize: '12px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '8px',
      marginInlineStart: '-4px',
    },
    [`& .${buttonClasses.endIcon}`]: {
      '& > *': {
        fontSize: '12px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '-4px',
      marginInlineStart: '8px',
    },
  },
  [`&.${buttonClasses.sizeSmall}`]: {
    height: '32px',
    ...typography.body2,
    [`& .${buttonClasses.startIcon}`]: {
      '& > *': {
        fontSize: '12px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '8px',
      marginInlineStart: '-2px',
    },
    [`& .${buttonClasses.endIcon}`]: {
      '& > *': {
        fontSize: '12px',
      },
      display: 'flex',
      alignItems: 'center',
      marginInlineEnd: '-2px',
      marginInlineStart: '8px',
    },
  },
  [`&.MuiButton-sizeXsmall`]: {
    [`& .${buttonClasses.startIcon}`]: {
      marginInlineEnd: '8px',
      marginInlineStart: '-4px',
    },
    [`& .${buttonClasses.endIcon}`]: {
      marginInlineEnd: '-4px',
      marginInlineStart: '8px',
    },
  },
})

/**
 * Returns the styles for the button based on the variant
 */
export const getButtonVariantPrimitives = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
      return {
        color: uiColors.content.white,
        backgroundColor: blue[9],
        '&:hover': {
          backgroundColor: blue[10],
        },
        '&:active': {
          boxShadow: cardShadow.normal,
          backgroundColor: blue[11],
        },
      }
    case 'secondary':
      return {
        color: uiColors.content.white,
        backgroundColor: gray[12],
        '&:hover': {
          boxShadow: cardShadow.hover,
          backgroundColor: gray[11],
        },
        '&:active': {
          boxShadow: cardShadow.active,
          backgroundColor: gray[10],
        },
      }
    case 'tertiary':
      return {
        color: uiColors.content.primary,
        backgroundColor: gray[2],
        '&:hover': {
          backgroundColor: gray[3],
        },
        '&:active': {
          backgroundColor: gray[4],
        },
      }
    case 'ghost':
      return {
        color: gray[10],
        backgroundColor: 'transparent',
        '&:hover': {
          backgroundColor: gray[2],
        },
        '&:active': {
          backgroundColor: gray[3],
        },
      }
    case 'true-ghost':
      return {
        color: uiColors.content.primary,
        '&:hover': {
          backgroundColor: 'transparent',
          color: uiColors.content.secondary,
        },
      }
    case 'danger':
      return {
        color: uiColors.content.white,
        backgroundColor: red[10],
        '&:hover': {
          boxShadow: cardShadow.hover,
          backgroundColor: red[11],
        },
        '&:active': {
          boxShadow: cardShadow.active,
          backgroundColor: red[12],
        },
      }
    case 'underline':
      return {
        textDecorationLine: 'underline',
        color: uiColors.content.primary,
        backgroundColor: 'transparent',
        '&:hover': {
          textDecorationLine: 'underline',
          color: blue[9],
          backgroundColor: 'transparent',
        },
        '&:active': {
          textDecorationLine: 'underline',
          color: blue[11],
        },
      }
    case 'cta':
      return {
        color: uiColors.content.white,
        backgroundColor: brightBlue,
        transition: 'transform 0.2s ease-in-out',
        '&:hover': {
          transform: 'scale(1.02)',
          backgroundColor: brightBlue,
          boxShadow: depth[3],
        },
        '&:active': {
          backgroundColor: brightBlue,
          boxShadow: depth[2],
        },
      }
  }
}

/**
 * Returns the disabled styles for the button based on the variant
 */
export const getButtonDisabledPrimitives = (variant: ButtonVariant) => {
  switch (variant) {
    case 'primary':
    case 'secondary':
    case 'tertiary':
    case 'danger':
    case 'cta':
      return {
        [`&.${buttonClasses.disabled}`]: {
          backgroundColor: gray[3],
          color: uiColors.content.disabled,
          outline: 'none',
        },
      }
    case 'ghost':
    case 'true-ghost':
      return {
        [`&.${buttonClasses.disabled}`]: {
          color: uiColors.content.disabled,
        },
      }
    case 'underline':
      return {
        [`&.${buttonClasses.disabled}`]: {
          color: uiColors.content.disabled,
          textDecorationLine: 'underline',
        },
      }
  }
}
